import React, { useRef } from 'react';
import { createPortal } from 'react-dom';

import { Post, type IPost } from '../../components/Post';
import { DealsLeadFormBlock, type IDealsLeadFormBlockProps } from '../../components/DealsLeadForm';
import { DealsBanner, type IDealsBannerProps } from '../../components/DealsBanner';
import { DealsExperiment } from '../../constants/dealsExperiment';
import { useFindDealsBannerMountNode, usePlaceBeforeTrailingCaption } from './utils';

/**
 * Группа эксперимента приходит пропом, а не вычисляется тут через DealsExperiment.use() —
 * PostContainer вызывает его безусловно, чтобы группу можно было учитывать и в
 * usePreparedContent (отключение сторонних баннеров только для группы B).
 * Форма и баннер показываются только в тестовой группе B — форма встраивается внутри
 * статьи перед хвостовыми `.caption` (или в конце контента, если caption нет), баннер —
 * в маркер `data-name="deal-experiment-banner"`, который редактор расставляет в тексте статьи.
 */
export const DealsExperimentArticle = ({
  dealsLeadForm,
  dealsBanner,
  dealsBannerContainerId,
  group,
  postProps,
}: {
  dealsLeadForm: IDealsLeadFormBlockProps;
  dealsBanner: IDealsBannerProps | undefined;
  dealsBannerContainerId: string | null;
  group: ReturnType<typeof DealsExperiment.use>;
  postProps: IPost;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const showForm = group === 'B';
  const formMountNode = usePlaceBeforeTrailingCaption(contentRef, showForm, postProps.content);

  const form = showForm ? <DealsLeadFormBlock {...dealsLeadForm} /> : null;
  const showBanner = showForm && Boolean(dealsBanner);
  const bannerMountNode = useFindDealsBannerMountNode(
    contentRef,
    showBanner,
    dealsBannerContainerId,
    postProps.content,
  );
  const banner = showBanner && dealsBanner ? <DealsBanner {...dealsBanner} /> : null;

  return (
    <>
      <Post {...postProps} contentRef={contentRef} />
      {form && formMountNode && createPortal(form, formMountNode)}
      {banner && bannerMountNode && createPortal(banner, bannerMountNode)}
    </>
  );
};
